import React, { useState, useEffect } from 'react';
import * as DateFns from 'date-fns';
import { Dropdown } from 'react-bootstrap';

import Table, { RowType } from '../Shared/CustomTable';
import { getTeamProps, stringIsNumber, isBlankString } from '../../helpers/helpers';
import Actions from '../../components/DataOps/ActionsDataOps';
import Progress from './Progress';
import { DATAOPS_ACTIONS_FILTER, SCOPE } from '../../config/constants';
import Loader from '../Shared/Loader';
import { PracticeDataOps } from './types';
import { TeamProps } from '../../config/commonTypes';

type DataOpsTableProps = {
  data: Array<PracticeDataOps>;
  activeChecked: boolean;
  handleModalOpening: (practice: PracticeDataOps)=>void;
}
type DataOpsActionsFilter = {
  value: string,
  label: string
}
export default ({ data, activeChecked, handleModalOpening }:DataOpsTableProps) => {

  const [nameFilter, setNameFilter] = useState('');
  const [datasetsFilter, setDatasetsFilter] = useState('');
  const [onTableData, setOnTableData] = useState<Array<PracticeDataOps>|null>(null);
  const [actionsFilter, setActionsFilter] = useState<DataOpsActionsFilter>({
    value: DATAOPS_ACTIONS_FILTER.default,
    label: DATAOPS_ACTIONS_FILTER.default
  });
  const filter = (name:string, datasets:string, actions:DataOpsActionsFilter) => {
    if (isBlankString(name) && isBlankString(datasets) && actions.value === DATAOPS_ACTIONS_FILTER.default) {
      setOnTableData(data);
      return;
    }
    const regexName = isBlankString(name) ? new RegExp('.*', 'gu') :
      new RegExp(name.trim(), 'gi');
    const regexDatasets = isBlankString(datasets) ? new RegExp('.*', 'gu') : new RegExp(datasets.trim(), 'gi');
    setOnTableData(data.filter(item => {
      let stillConsidering = true;
      if (name.length > 0) {
        if (item.name) {
          //@ts-ignore
          stillConsidering &= item.name.search(regexName) !== -1;
        } else {
          return false;
        }
      }
      if (datasets.length > 0) {
        if (item.dataset_id && item.dataset_id) {
          //@ts-ignore
          stillConsidering &= item.dataset_id.search(regexDatasets) !== -1;
        } else {
          return false;
        }
      }
      if (actions.value !== DATAOPS_ACTIONS_FILTER.default) {
        if (item.status_replicaInfo) {
          if (item.status_replicaInfo !== actions.value) {
            return false;
          }
        } else {
          return false;
        }
      }
      return stillConsidering;
    }));
  };

  const nameFilterHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      setNameFilter(event.target.value);
      filter(event.target.value, datasetsFilter, actionsFilter);
    }
  };

  const datasetsFilterHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ' && (stringIsNumber(event.target.value) || event.target.value === '')) {
      setDatasetsFilter(event.target.value);
      filter(nameFilter, event.target.value, actionsFilter);
    }
  };

  const actionsFilterHandler = (value:DataOpsActionsFilter) => {
    setActionsFilter(value);
    filter(nameFilter, datasetsFilter, value);
  };

  const actionsFilterDropdownItems = DATAOPS_ACTIONS_FILTER.types.map((type, index) =>
    <Dropdown.Item key={index} onSelect={() => actionsFilterHandler(type)} active={actionsFilter.label === type.label}>
      {type.label}
    </Dropdown.Item>
  );

  useEffect(() => {
    setOnTableData(data);
    filter(nameFilter, datasetsFilter, actionsFilter);
  }, [data]);
  // max-width = 1276px
  const headers = [
    {
      content: 'TEAM',
      style: { width: '88px', verticalAlign: 'top' },
      class: 't-ll',
      isOrderly: true
    },
    {
      content: 'NAME',
      style: { width: '148px' },
      class: 't-ll', isOrderly: true,
      print: (content:string) => (
        <>
          {content}
          <input
            className="input-02"
            type="text" placeholder="Search" value={nameFilter}
            onClick={e => e.stopPropagation()}
            onChange={(event) => nameFilterHandler(event)}
          />
        </>
      )
    },
    {
      content: 'IP',
      style: { width: '131px', verticalAlign: 'top' },
      class: 't-ll',
      isOrderly: true
    },
    {
      content: 'DATASETS',
      style: { width: '149px' },
      class: 't-ll', isOrderly: true,
      print: (content:string) => <>{content}<input
        className="input-04"
        type="text" placeholder="Search" value={datasetsFilter}
        onClick={e => e.stopPropagation()}
        onChange={(event) => datasetsFilterHandler(event)}
      /></>
    },
    {
      content: 'START TIME',
      style: { width: '116px', verticalAlign: 'top' },
      class: 't-ll',
      isOrderly: true
    },
    {
      content: 'END TIME',
      style: { width: '165px', verticalAlign: 'top' },
      class: 't-ll',
      isOrderly: true
    },
    {
      content: 'LAST EDITED',
      style: { width: '171px', verticalAlign: 'top' },
      class: 't-ll',
      isOrderly: true
    },
    { content: 'PROGRESS', style: { width: '105px', verticalAlign: 'top' }, class: 't-ll' },
    {
      content: 'ACTIONS',
      style: { width: '199px', verticalAlign: 'top', padding: '0px 9px !important' },
      print: (content:string) => (<>{content}
        <div onClick={event => event.stopPropagation()}>
          <Dropdown className="actions-dataops-filter">
            {/*@ts-ignore*/}
            <Dropdown.Toggle>
              {actionsFilter.label}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {actionsFilterDropdownItems}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </>)
    }
  ];
  let rows:Array<RowType> = [];
  if (onTableData) {
    if (activeChecked) {
      data = data.filter((practice) => {
        return practice.status_replicaInfo === 'R_I' || practice.status_replicaInfo === 'B_I';
      });
    }
    rows = onTableData.map((practice, index) => {
      const teamOfPractice:TeamProps  = getTeamProps(practice.home_team_id) ? getTeamProps(practice.home_team_id) : { teamAbbr: 'CC' };
      return {
        columns: [
          {
            content: teamOfPractice!.teamAbbr,
            class: 't-ll',
            style: { textAlign: 'center' },
            print: () => (
              <img
                alt="gameimg"
                src={'/assets/images/logos/' + teamOfPractice!.teamAbbr + '.svg'}
                height="40"
                width="40"
              />
            )
          },
          { content: practice.name, class: 't-ll', print: (content:string) => <label className="pp-ww">{content}</label> },
          {
            content: practice.ip_replicaInfo ? practice.ip_replicaInfo : '',
            print: (content:string) => <label className="pp-ww"><u>{content}</u></label>,
            class: 't-ll'
          },
          { content: practice.dataset_id, print: (content:string) => <label className="pp-ww">{content}</label>, class: 't-ll' },
          {
            content: practice.start_time,
            print: (content:number) => (
              <>
                <p style={{ marginBottom: '-3px', marginTop: '-5px' }}>
                  {DateFns.format(new Date(content), 'MM/dd/yyyy')}
                </p>
                <p style={{ marginBottom: '-6px' }}>{DateFns.format(new Date(content), `hh:mm:ss aaaaa'm'`)}</p>
              </>
            ),
            class: 't-ll'
          },
          {
            content: practice.end_time,
            print: (content:number) => {
              let time:string|any = '';
              if (content) {
                time = <>
                  <p
                    style={{
                      marginBottom: '-3px',
                      marginTop: '-5px'
                    }}
                  >{DateFns.format(new Date(content), 'MM/dd/yyyy')}</p>
                  <p style={{ marginBottom: '-6px' }}>{DateFns.format(new Date(content), `hh:mm:ss aaaaa'm'`)}</p>
                </>;
              }
              return time;
            },
            class: 't-ll'
          },
          {
            content: practice.update_timestamp,
            print: (content:number) => (
              <label className="pp-ww">
                {DateFns.format(new Date(content), `MM/dd/yyyy hh:mm:ss aaaaa'm'`)}
              </label>
            ),
            style: { letterSpacing: '-0.7px' },
            class: 't-ll'
          },
          { content: practice.status_replicaInfo, print: (content:string) => <Progress status={content} />, class: 't-ll' },
          // @ts-ignore
          { content: practice, print: (content:PracticeDataOps) => <Actions practice={content} handleModalOpening={handleModalOpening} />, class: 'data-oo' }
        ],
        id: practice._id
      };
    });
  }
  return (
    <Loader>
      <Table
        classTable="table-with-search-header dataops-table"
        headers={headers}
        rows={rows}
        default={{ orderColumn: 4 }}
        forceScope={SCOPE.DATAOPS.PAGE}
      />
    </Loader>
  );
};
